import {useCallback, useEffect, useEffectEvent, useMemo, useRef, useState} from 'react';

import {activeSelectionRange, selectionCoversTextOutside} from 'lib/domSelection';
import {isHighlightApiSupported} from 'lib/isHighlightApiSupported';
import {hasUntypesetMath} from 'lib/mathJaxDom';

import {useAnnotatableContentPresence, useAnnotationModeStore} from '../store/AnnotationModeStore';
import {buildAnchor, isAnchoredInLang, isMarkableRange} from '../engine/annotationAnchors';
import {
  combinedNoteOf,
  computeAnnotationMerge,
  countNotedMarks,
  findContainingAnnotation
} from '../engine/annotationMerge';
import {
  annotationIdOfMark,
  findMarkFocusStop,
  isRegionWrapped,
  unwrapRegionMarks,
  wrapRegionMarks
} from '../engine/annotationMarkElements';
import {announceAnnotationChange} from '../shared/AnnotationAnnouncer';
import {announceMarkChange} from '../shared/announceMarkChange';
import {
  describeMarkAdded,
  describeMarkExtended,
  describeMarkUnchanged,
  describeMarksMerged
} from '../shared/annotationAnnouncements';
import {exceedsNoteLimit} from '../shared/annotationRules';
import {selectVisibleRegionAnnotations} from '../store/annotationSelectors';
import {
  clearRegionHighlights,
  clearRegionHoverHighlights,
  hasRegionHighlights,
  setRegionHighlights
} from '../engine/annotationPaintCoordinator';
import type {TextProjection} from '../engine/annotationTextProjection';
import type {Annotation, AnnotationAnchor, AnnotationMerge, AnnotationRange} from '../types';
import {
  annotationAnchorAtPoint,
  findAnnotationAtPoint,
  getSelectionNormalizedRange,
  resolveAnnotationRanges,
  resolveMarks,
  selectionCoversNestedRegion
} from '../engine/annotationUtils';
import {useIsAnnotationModeAvailable} from '../mode/useIsAnnotationModeAvailable';
import {useRegionEdit} from '../store/useRegionEdit';
import {useTextProjection} from '../engine/useTextProjection';

/**
 * `containerRef` is a callback ref so the effects re-run if the element
 * attaches late (e.g. the feature flag resolving after first render).
 */
export function useAnnotations(
  regionId: string,
  lang: string | null = null,
  {repaintOnMove = false}: {repaintOnMove?: boolean} = {}
): {
  /** The region's marks on the rendered language, as painted. */
  annotations: Annotation[];
  captureAnnotation: (x: number, y: number) => AnnotationAnchor | null;
  getAnnotationAtPoint: (x: number, y: number) => AnnotationAnchor | null;
  containerRef: (element: HTMLElement | null) => void;
  /** The region element once it has attached; null before that. */
  container: HTMLElement | null;
  getProjection: () => TextProjection | null;
  isMergeConfirmPending: boolean;
  /** True when the pending merge's combined note is longer than the server accepts. */
  isMergeNoteTooLong: boolean;
  /** Applies the pending merge; returns the merged mark so its popover can open. */
  confirmPendingMerge: () => AnnotationAnchor | null;
  cancelPendingMerge: () => void;
} {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const [pendingMerge, openPendingMerge, cancelPendingMerge] = useRegionEdit<{
    merge: AnnotationMerge;
    rect: DOMRect;
  }>();
  const storedAnnotations = useAnnotationModeStore((state) =>
    selectVisibleRegionAnnotations(state, regionId)
  );
  // `repaint` depends on this: a fresh array each render would rebuild every
  // Highlight in the document.
  const annotations = useMemo(
    () => storedAnnotations.filter(({anchor}) => isAnchoredInLang(anchor, lang)),
    [storedAnnotations, lang]
  );
  const isMergeNoteTooLong =
    pendingMerge !== null && exceedsNoteLimit(combinedNoteOf(annotations, pendingMerge.merge));
  const isAnnotationModeOn = useAnnotationModeStore((state) => state.isAnnotationModeOn);
  if (!isAnnotationModeOn && pendingMerge) {
    cancelPendingMerge();
  }
  const areMarksFocusable = useIsAnnotationModeAvailable() && isAnnotationModeOn;
  // A real mutation unwraps the marks, which drops focus from the one being read.
  const lostFocusMarkIdRef = useRef<string | null>(null);
  const wrapSignatureRef = useRef('');
  const addAnnotation = useAnnotationModeStore((state) => state.addAnnotation);
  const mergeAnnotations = useAnnotationModeStore((state) => state.mergeAnnotations);
  useAnnotatableContentPresence(Boolean(container?.textContent?.trim()));

  const {getProjection, invalidateProjection} = useTextProjection(container, {
    onMutate: () => {
      if (container) {
        lostFocusMarkIdRef.current ??= annotationIdOfMark(document.activeElement, container);
        unwrapRegionMarks(container);
      }
      clearRegionHighlights(regionId);
      clearRegionHoverHighlights(regionId);
    },
    // Deferred closure: `repaint` is declared below; the hook reads handlers
    // through a ref, so the latest one always fires.
    onSettle: () => repaint()
  });

  const repaint = useCallback(() => {
    if (!container || !isHighlightApiSupported()) {
      return;
    }
    const signature = JSON.stringify([areMarksFocusable, annotations]);
    // Rewrapping resets live Ranges in the region, so an unchanged wrap is left alone.
    if (isRegionWrapped(container) && wrapSignatureRef.current === signature) {
      const projection = getProjection();
      setRegionHighlights(
        regionId,
        projection ? resolveAnnotationRanges(projection, annotations) : new Map()
      );
      return;
    }
    wrapSignatureRef.current = signature;
    const focusedMarkId =
      annotationIdOfMark(document.activeElement, container) ??
      (document.activeElement === document.body ? lostFocusMarkIdRef.current : null);
    lostFocusMarkIdRef.current = null;
    unwrapRegionMarks(container);
    invalidateProjection();
    let projection = annotations.length > 0 ? getProjection() : null;
    if (projection) {
      wrapRegionMarks(container, projection, resolveMarks(projection, annotations), {
        focusable: areMarksFocusable
      });
      invalidateProjection();
      projection = getProjection();
    }
    setRegionHighlights(
      regionId,
      projection ? resolveAnnotationRanges(projection, annotations) : new Map()
    );
    if (focusedMarkId) {
      findMarkFocusStop(container, focusedMarkId)?.focus({preventScroll: true});
    }
  }, [container, regionId, annotations, getProjection, invalidateProjection, areMarksFocusable]);

  /** The mark this selection lands on, applying the #19010 overlap policy. */
  const addOrMergeAnnotation = useCallback(
    (projection: TextProjection, range: AnnotationRange, rect: DOMRect): string | null => {
      // Each gesture answers for itself; a question left over from an earlier one
      // must never outlive it.
      cancelPendingMerge();
      const containing = findContainingAnnotation(projection, annotations, range, lang);
      if (containing) {
        announceAnnotationChange(describeMarkUnchanged(containing));
        return containing.id;
      }

      const merge = computeAnnotationMerge(projection, annotations, range, lang);
      if (merge) {
        if (merge.absorbedIds.length > 0) {
          openPendingMerge({merge, rect}, regionId);
          return null;
        }
        mergeAnnotations(merge);
        announceMarkChange(regionId, merge.intoId, describeMarkExtended);
        return merge.intoId;
      }

      const anchor = buildAnchor(projection, range, {regionId, lang});
      if (!anchor) {
        return null;
      }
      const id = addAnnotation(anchor);
      announceMarkChange(regionId, id, describeMarkAdded);
      return id;
    },
    [
      annotations,
      lang,
      regionId,
      addAnnotation,
      mergeAnnotations,
      openPendingMerge,
      cancelPendingMerge
    ]
  );

  const captureAnnotation = useCallback(
    (x: number, y: number): AnnotationAnchor | null => {
      // Selection first, so a plain click never pays for a projection build.
      const selection = activeSelectionRange();
      if (!selection || !container) return null;
      // A drag across a region boundary — out of this one, or into a nested one —
      // is rejected outright, never clamped to the part this region happens to cover.
      if (selectionCoversTextOutside(selection, container)) return null;
      if (selectionCoversNestedRegion(selection, container)) return null;
      const projection = getProjection();
      const range = projection && getSelectionNormalizedRange(projection);
      if (!projection || !range || !isMarkableRange(projection, range)) return null;

      // The popover hangs below its anchor, so it anchors to the release point:
      // a selection's own box can be taller than the viewport.
      const rect = new DOMRect(x, y);
      const id = addOrMergeAnnotation(projection, range, rect);
      if (!id) return null;
      // Consume the selection only when the gesture landed, so a failed capture
      // stays selectable for copying.
      window.getSelection()?.removeAllRanges();
      return {rect, id};
    },
    [container, getProjection, addOrMergeAnnotation]
  );

  const confirmPendingMerge = useCallback((): AnnotationAnchor | null => {
    if (!pendingMerge || isMergeNoteTooLong) {
      return null;
    }
    const {merge, rect} = pendingMerge;
    const notedCount = countNotedMarks(annotations, merge);
    mergeAnnotations(merge);
    window.getSelection()?.removeAllRanges();
    cancelPendingMerge();
    announceMarkChange(regionId, merge.intoId, (mark, subject) =>
      describeMarksMerged(mark, notedCount, subject)
    );
    return {rect, id: merge.intoId};
  }, [
    pendingMerge,
    isMergeNoteTooLong,
    annotations,
    regionId,
    mergeAnnotations,
    cancelPendingMerge
  ]);

  const getAnnotationAtPoint = useCallback(
    (x: number, y: number): AnnotationAnchor | null => {
      const projection = annotations.length > 0 ? getProjection() : null;
      if (!container || !projection) {
        return null;
      }
      const id = findAnnotationAtPoint(container, projection, x, y, annotations);
      return id ? annotationAnchorAtPoint(x, y, id) : null;
    },
    [container, getProjection, annotations]
  );

  useEffect(() => {
    // With nothing painted yet, MathJax rewrites are waited out (no flash
    // against stale DOM); once painted, annotation changes always resync.
    if (container && (hasRegionHighlights(regionId) || !hasUntypesetMath(container))) {
      repaint();
    }
  }, [container, regionId, repaint]);

  // Moving the region's DOM (a reorder) collapses its live Ranges; repaint where it lands.
  const repaintAfterMove = useEffectEvent(() => repaint());
  useEffect(() => {
    if (!container || !repaintOnMove) {
      return undefined;
    }
    const observer = new MutationObserver((records) => {
      const landed = records.some(({addedNodes}) =>
        Array.from(addedNodes).some((node) => node.contains(container))
      );
      if (landed) {
        repaintAfterMove();
      }
    });
    observer.observe(container.ownerDocument.body, {childList: true, subtree: true});
    return () => observer.disconnect();
  }, [container, repaintOnMove]);

  // The region's paint dies with its container (unmount, region switch, detach).
  useEffect(() => {
    if (!container) {
      return undefined;
    }
    return () => {
      unwrapRegionMarks(container);
      clearRegionHighlights(regionId);
    };
  }, [container, regionId]);

  return {
    annotations,
    captureAnnotation,
    getAnnotationAtPoint,
    containerRef: setContainer,
    container,
    getProjection,
    isMergeConfirmPending: isAnnotationModeOn && pendingMerge !== null,
    isMergeNoteTooLong,
    confirmPendingMerge,
    cancelPendingMerge
  };
}
